import { LoadingSkeleton } from "MVVM/Components/Skeloton";
import { useInfiniteScroll } from "MVVM/Hooks/useInfiniteScroll";
import { FeedItem } from "./FeedItem";
import { useActivityFeed } from "./ViewModel";
import { useAutoplayObserver } from "./useAutoplayObserver";
import { IActivityFeedNode } from "./@types";
import {
  EmptyState,
  ErrorState,
  FeedHeader,
  FeedList,
  FeedSubtitle,
  FeedTitle,
  LoadingMoreRow,
  LoadMoreError,
  RetryButton,
  SentinelRow,
  Spinner,
} from "./styledComponents";

const nodeKey = (node: IActivityFeedNode, index: number) => {
  const { item } = node;
  const itemId = item.__typename === "Clip" ? item.shareId : item.id;
  return `${item.__typename}-${itemId}-${node.occurredAt}-${index}`;
};

export const ActivityFeed = () => {
  const {
    nodes,
    loading,
    error,
    loadMoreError,
    hasNextPage,
    loadMore,
    refetch,
    togglePin,
    pinError,
  } = useActivityFeed();

  const loadMoreRef = useInfiniteScroll({
    onLoadMore: loadMore,
    hasNextPage,
    isLoading: loading,
    trigger: nodes.length,
    // Default (200px) meant the sentinel had to nearly reach the viewport
    // before the next page kicked off, so scrolling caught up to the fetch
    // and it read as "stuck." The query itself takes a few seconds, so even
    // 1000px wasn't consistently enough lead time — bumped further to give
    // it room to finish before the sentinel is actually reached.
    threshold: 2500,
  });

  const { activeKey, registerRef } = useAutoplayObserver();
  const activeIndex = nodes.findIndex(
    (node, index) => nodeKey(node, index) === activeKey,
  );

  return (
    <div>
      <FeedHeader>
        <FeedTitle>Your feed</FeedTitle>
        <FeedSubtitle>Latest from you, friends &amp; Allstar</FeedSubtitle>
      </FeedHeader>

      {loading && nodes.length === 0 ? (
        <FeedList>
          {[0, 1, 2].map((i) => (
            <LoadingSkeleton
              key={i}
              style={{ borderRadius: 12, height: 220 }}
            />
          ))}
        </FeedList>
      ) : error && nodes.length === 0 ? (
        <ErrorState>
          Couldn&apos;t load your feed.
          <RetryButton onClick={() => refetch()}>Try again</RetryButton>
        </ErrorState>
      ) : nodes.length === 0 ? (
        <EmptyState>No activity yet.</EmptyState>
      ) : (
        <FeedList>
          {nodes.map((node, index) => {
            const key = nodeKey(node, index);
            return (
              <FeedItem
                key={key}
                node={node}
                isActive={key === activeKey}
                // Warm the adjacent cards' video ahead of time — the viewer
                // scrolls both directions, so preload whichever neighbor
                // could be next either way.
                preload={
                  activeIndex !== -1 && Math.abs(index - activeIndex) === 1
                }
                registerRef={registerRef(key)}
                onTogglePin={togglePin}
                pinError={
                  pinError?.itemId === node.item?.id ? pinError.message : null
                }
              />
            );
          })}
          {loadMoreError ? (
            <LoadMoreError>
              Couldn&apos;t load more.
              <RetryButton onClick={loadMore}>Try again</RetryButton>
            </LoadMoreError>
          ) : loading ? (
            <LoadingMoreRow>
              <Spinner />
            </LoadingMoreRow>
          ) : (
            <SentinelRow ref={loadMoreRef} />
          )}
        </FeedList>
      )}
    </div>
  );
};
